import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { applyCpfCnpjMask, formatBrlNumber } from '~/lib/masks';
import { useWizard } from '~/pages/emitir/wizardContext';
import type { ValorInput } from '~/schemas/invoice';

interface StepRevisaoProps {
  apiError: string | null;
}

interface RetencaoConfig {
  key: keyof ValorInput['retencoes'];
  aliquotaKey: keyof ValorInput['retencoes'];
  label: string;
}

const RETENCOES: readonly RetencaoConfig[] = [
  { key: 'irrf', aliquotaKey: 'irrf_aliquota', label: 'IRRF' },
  { key: 'pis', aliquotaKey: 'pis_aliquota', label: 'PIS' },
  { key: 'cofins', aliquotaKey: 'cofins_aliquota', label: 'COFINS' },
  { key: 'csll', aliquotaKey: 'csll_aliquota', label: 'CSLL' },
  { key: 'inss', aliquotaKey: 'inss_aliquota', label: 'INSS' },
];

export function StepRevisao({ apiError }: StepRevisaoProps): JSX.Element {
  const { draft } = useWizard();
  const { tomador, servico, valor } = draft;

  const retencoesAtivas = RETENCOES.map((config) => {
    const ativo = valor.retencoes[config.key] as boolean;
    if (!ativo) return null;
    const aliquota = valor.retencoes[config.aliquotaKey] as number;
    const valorRetido = (valor.valor_servico * aliquota) / 100;
    return { ...config, aliquota, valorRetido };
  }).filter((item): item is NonNullable<typeof item> => item !== null);

  const totalRetencoes = retencoesAtivas.reduce((sum, item) => sum + item.valorRetido, 0);
  const valorIss = (valor.valor_servico * valor.iss_aliquota) / 100;
  const valorLiquido = valor.valor_servico - totalRetencoes;

  const endereco = [tomador.logradouro, tomador.numero, tomador.complemento, tomador.bairro]
    .filter(Boolean)
    .join(', ');
  const cidadeUf = [tomador.descricao_cidade, tomador.estado].filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {apiError && (
        <div
          style={{
            padding: '12px 14px',
            background: 'var(--rx-negative-tint)',
            border: '0.5px solid var(--rx-negative)',
            borderRadius: 12,
            display: 'flex',
            gap: 10,
          }}
        >
          <Icon name="warn" size={16} color="var(--rx-negative)" />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: 'var(--rx-negative)',
              }}
            >
              Não foi possível enviar
            </div>
            <p
              style={{
                marginTop: 4,
                fontSize: 13,
                color: 'var(--rx-text)',
                whiteSpace: 'pre-line',
              }}
            >
              {apiError}
            </p>
          </div>
        </div>
      )}

      {/* Resumo do valor — bloco brand verde como hero pequeno */}
      <div
        style={{
          background: 'var(--rx-brand)',
          color: '#fff',
          borderRadius: 18,
          padding: '18px 20px 20px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              opacity: 0.7,
            }}
          >
            Valor líquido a receber
          </span>
          <span className="rx-mono" style={{ fontSize: 11, opacity: 0.8 }}>
            ISS {valor.iss_aliquota}%
          </span>
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontSize: 13, opacity: 0.6 }}>R$</span>
          <span
            className="rx-mono"
            style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.8px' }}
          >
            {formatBrlNumber(valorLiquido)}
          </span>
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 10,
            borderTop: '0.5px solid rgba(255,255,255,0.18)',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            opacity: 0.85,
          }}
        >
          <span className="rx-mono">Bruto R$ {formatBrlNumber(valor.valor_servico)}</span>
          {totalRetencoes > 0 && (
            <span className="rx-mono">− Retenções R$ {formatBrlNumber(totalRetencoes)}</span>
          )}
        </div>
      </div>

      <SectionHead label="Tomador" />
      <DetailCard>
        {(() => {
          const rows: { label: string; value: string; mono?: boolean }[] = [
            { label: 'Razão social', value: tomador.razao_social || '—' },
            { label: 'CPF/CNPJ', value: applyCpfCnpjMask(tomador.cpf_cnpj), mono: true },
          ];
          if (tomador.email) rows.push({ label: 'E-mail', value: tomador.email });
          if (endereco) rows.push({ label: 'Endereço', value: endereco });
          if (cidadeUf) rows.push({ label: 'Município', value: cidadeUf });
          return rows.map((r, idx) => (
            <Row key={r.label} label={r.label} value={r.value} mono={r.mono} first={idx === 0} />
          ));
        })()}
      </DetailCard>

      <SectionHead label="Serviço" />
      <DetailCard>
        <div style={{ padding: '13px 16px' }}>
          <div
            className="rx-mono"
            style={{ fontSize: 12, color: 'var(--rx-brand)', fontWeight: 600 }}
          >
            {servico.codigo_servico}
          </div>
          <p
            style={{
              marginTop: 6,
              fontSize: 13,
              color: 'var(--rx-text)',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
            }}
          >
            {servico.discriminacao}
          </p>
        </div>
      </DetailCard>

      <SectionHead label="Tributação" />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '14px 16px',
        }}
      >
        <Breakdown
          label="Valor do serviço"
          value={`R$ ${formatBrlNumber(valor.valor_servico)}`}
          first
        />
        <Breakdown
          label={`ISS ${valor.iss_aliquota}%${valor.iss_retido ? ' · retido' : ''}`}
          value={`R$ ${formatBrlNumber(valorIss)}`}
        />
        {retencoesAtivas.map((item) => (
          <Breakdown
            key={item.key}
            label={`${item.label} ${item.aliquota}%`}
            value={`− R$ ${formatBrlNumber(item.valorRetido)}`}
            muted
          />
        ))}
        <div
          style={{
            marginTop: 10,
            paddingTop: 12,
            borderTop: '0.5px solid var(--rx-border-strong)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.3px',
              color: 'var(--rx-text)',
            }}
          >
            Valor líquido
          </span>
          <span
            className="rx-mono"
            style={{ fontSize: 16, fontWeight: 600, color: 'var(--rx-brand)' }}
          >
            R$ {formatBrlNumber(valorLiquido)}
          </span>
        </div>
      </div>

      {/* Hint final */}
      <div
        style={{
          marginTop: 4,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          background: 'var(--rx-surface-alt)',
          borderRadius: 12,
        }}
      >
        <Icon name="shield" size={16} color="var(--rx-text-3)" />
        <p style={{ flex: 1, fontSize: 11, color: 'var(--rx-text-2)', lineHeight: 1.45 }}>
          Ao confirmar, enviamos a nota à prefeitura para processamento. O status fica em tempo real
          em "Minhas notas".
        </p>
      </div>
    </div>
  );
}

function DetailCard({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

interface RowProps {
  label: string;
  value: string;
  mono?: boolean;
  first?: boolean;
}

function Row({ label, value, mono, first }: RowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 16px',
        borderTop: first ? 'none' : '0.5px solid var(--rx-border)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--rx-text-3)', fontWeight: 500, flexShrink: 0 }}>
        {label}
      </span>
      <span
        className={mono ? 'rx-mono' : ''}
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--rx-text)',
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
}

interface BreakdownProps {
  label: string;
  value: string;
  first?: boolean;
  muted?: boolean;
}

function Breakdown({ label, value, first, muted }: BreakdownProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '7px 0',
        borderTop: first ? 'none' : '0.5px solid var(--rx-border)',
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: muted ? 'var(--rx-text-2)' : 'var(--rx-text)',
        }}
      >
        {label}
      </span>
      <span
        className="rx-mono"
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: muted ? 'var(--rx-text-2)' : 'var(--rx-text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
